import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users, Music, Building, Banknote } from "lucide-react";
import { StatCard, SectionHeader, ProgressRow } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState } from "@/components/dashboard/common/PageShell";
import { useAdminProfiles, useAdminOrders, useAdminCounts, toDailySeries } from "@/hooks/admin/useAdminMetrics";

const RANGES = [7, 30, 90];

const chartTooltip = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  color: "hsl(var(--foreground))",
};

const GlobalAnalytics = () => {
  const [range, setRange] = useState(30);
  const { data: profiles = [], isLoading, error, refetch } = useAdminProfiles();
  const { data: orders = [] } = useAdminOrders();
  const { data: counts } = useAdminCounts();

  const signups = useMemo(
    () => toDailySeries(profiles.filter((p) => p.created_at).map((p) => ({ created_at: p.created_at as string })), range),
    [profiles, range],
  );

  const revenue = useMemo(
    () => toDailySeries(orders.filter((o) => o.status === "paid"), range, (o) => Number(o.total ?? 0)),
    [orders, range],
  );

  const rangeTotals = useMemo(() => {
    const signupTotal = signups.reduce((s, d) => s + d.value, 0);
    const revenueTotal = revenue.reduce((s, d) => s + d.value, 0);
    const paidOrders = revenue.filter((d) => d.value > 0).length;
    return { signupTotal, revenueTotal, paidOrders };
  }, [signups, revenue]);

  const cities = useMemo(() => {
    const map = new Map<string, number>();
    profiles.forEach((p) => p.city && map.set(p.city, (map.get(p.city) ?? 0) + 1));
    return Array.from(map, ([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [profiles]);

  const orderStatus = useMemo(() => {
    const map = new Map<string, number>();
    orders.forEach((o) => map.set(o.status, (map.get(o.status) ?? 0) + 1));
    return Array.from(map, ([status, count]) => ({ status, count }));
  }, [orders]);

  if (isLoading) return <LoadingState label="Loading analytics…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Global analytics"
        description="Growth, revenue and geographic performance across every role on the platform."
        actions={
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <Button key={r} size="sm" variant={r === range ? "default" : "outline"} onClick={() => setRange(r)}>
                {r}d
              </Button>
            ))}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={`Signups · ${range}d`} value={rangeTotals.signupTotal} icon={Users} />
        <StatCard
          label={`Revenue · ${range}d`}
          value={`R${rangeTotals.revenueTotal.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`}
          icon={Banknote}
          tone="positive"
        />
        <StatCard label="Creators" value={profiles.filter((p) => p.user_type === "creator").length} icon={Music} />
        <StatCard label="Venues" value={profiles.filter((p) => p.user_type === "venue").length} icon={Building} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Account growth</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={signups}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltip} />
                <Line type="monotone" dataKey="value" name="Signups" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Revenue (ZAR)</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={chartTooltip} />
                <Bar dataKey="value" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Geographic distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {cities.length === 0 ? (
              <p className="text-sm text-muted-foreground">No city data captured yet.</p>
            ) : (
              cities.map((c) => <ProgressRow key={c.city} label={c.city} value={c.count} total={profiles.length || 1} />)
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Marketplace activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: "Events", value: counts?.events ?? 0 },
              { label: "Tickets", value: counts?.tickets ?? 0 },
              { label: "Products", value: counts?.products ?? 0 },
              { label: "Posts", value: counts?.posts ?? 0 },
              { label: "Job listings", value: counts?.job_listings ?? 0 },
              { label: "Reviews", value: counts?.reviews ?? 0 },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="text-sm text-foreground">{row.label}</span>
                <span className="text-sm font-semibold text-foreground">{row.value}</span>
              </div>
            ))}
            <div className="pt-2">
              <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Order status mix</p>
              <div className="flex flex-wrap gap-2">
                {orderStatus.length === 0 ? (
                  <span className="text-sm text-muted-foreground">No orders yet.</span>
                ) : (
                  orderStatus.map((o) => (
                    <span key={o.status} className="rounded-full border border-border px-3 py-1 text-xs capitalize text-foreground">
                      {o.status}: {o.count}
                    </span>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GlobalAnalytics;
